import * as signalR from '@microsoft/signalr';
import { apiClient } from '../api/client';

// SignalR message types
export interface LocationUpdateMessage {
  userId: string;
  displayName: string;
  latitude: number;
  longitude: number;
  heading?: number;
  accuracy: number;
  speed?: number;
  role: string;
  lastUpdated: string;
  connectionStatus: 'connected' | 'weak' | 'disconnected';
}

export interface HuntStateChangedMessage {
  huntId: string;
  previousStatus: string;
  newStatus: string;
  timestamp: string;
}

export interface ParticipantJoinedMessage {
  userId: string;
  displayName: string;
  role: 'hider' | 'hunter';
  joinedAt: string;
  isGuest?: boolean;
}

export interface ParticipantLeftMessage {
  userId: string;
  displayName: string;
  reason: 'left' | 'disconnected' | 'kicked';
}

export interface ParticipantRemovedMessage {
  userId: string;
  displayName: string;
  justification?: string;
}

export interface ParticipantReadyMessage {
  userId: string;
  displayName: string;
  isReady: boolean;
  allHidersReady: boolean;
}

export interface PinCreatedMessage {
  pinId: string;
  latitude: number;
  longitude: number;
  itemCount: number;
  sequenceOrder: number;
  createdBy: string;
}

export interface PinDeletedMessage {
  pinId: string;
  huntId: string;
  deletedBy: string;
}

export interface PinDiscoveredMessage {
  hunterId: string;
  hunterName: string;
  pinId: string;
  pinIndex: number;
  discoveredAt: string;
}

export interface ItemFoundMessage {
  hunterId: string;
  hunterName: string;
  pinId: string;
  itemIndex: number;
  totalFound: number;
}

export interface HunterCompletedMessage {
  hunterId: string;
  hunterName: string;
  completionTime: string;
  totalItems: number;
  rank: number;
}

export interface HuntCompletedMessage {
  huntId: string;
  completedAt: string;
  statsAvailableUntil: string;
}

export interface HuntDeletedMessage {
  huntId: string;
  huntName: string;
  message: string;
}

export interface ConnectionStatusMessage {
  userId: string;
  status: 'connected' | 'weak' | 'disconnected';
  lastSeen: string;
}

// Challenge-related messages
export interface ChallengeEncounteredMessage {
  pinId: string;
  pinIndex: number;
  challengeType: 'riddle' | 'hex_code' | 'numerical';
  challengeQuestion: string;
  captainUserId: string;
  captainDisplayName: string;
  encounterByUserId: string;
  encounterByDisplayName: string;
}

export interface ChallengeAttemptMessage {
  pinId: string;
  captainUserId: string;
  captainDisplayName: string;
  answer: string;
  isCorrect: boolean;
  timestamp: string;
}

export interface ChallengeSolvedMessage {
  pinId: string;
  solvedByUserId: string;
  solvedByDisplayName: string;
  solvedAt: string;
}

// Hunt starting countdown message
export interface HuntStartingMessage {
  huntId: string;
  previousStatus: string;
  newStatus: string;
  countdownSeconds: number;
  startingAt: string;
}

// Start cancelled message
export interface StartCancelledMessage {
  huntId: string;
  cancelledBy: string;
  cancelledByDisplayName: string;
  previousStatus: string;
  newStatus: string;
  timestamp: string;
}

// Validation messages - for hider referee feature
export interface ValidationRequestedMessage {
  huntId: string;
  pinId: string;
  pinIndex: number;
  requestedBy: string;
  requestedByDisplayName: string;
  requestedAt: string;
}

export interface ValidationApprovedMessage {
  huntId: string;
  pinId: string;
  pinIndex: number;
  approvedBy: string;
  approvedByDisplayName: string;
  approvedAt: string;
}

export interface ValidationApprovedWithChallengeMessage {
  huntId: string;
  pinId: string;
  pinIndex: number;
  hasChallenge: boolean;
  challengeType: 'riddle' | 'hex_code' | 'numerical';
  challengeQuestion: string;
}

export interface ValidationRejectedMessage {
  huntId: string;
  pinId: string;
  pinIndex: number;
  rejectedBy: string;
  rejectedByDisplayName: string;
  rejectedAt: string;
  newFoundCount: number;
  itemCount: number;
}

// Event handler types
type MessageHandler<T> = (message: T) => void;

export interface SignalREventHandlers {
  onLocationUpdate?: MessageHandler<LocationUpdateMessage>;
  onHuntStateChanged?: MessageHandler<HuntStateChangedMessage>;
  onParticipantJoined?: MessageHandler<ParticipantJoinedMessage>;
  onParticipantLeft?: MessageHandler<ParticipantLeftMessage>;
  onParticipantRemoved?: MessageHandler<ParticipantRemovedMessage>;
  onParticipantReady?: MessageHandler<ParticipantReadyMessage>;
  onPinCreated?: MessageHandler<PinCreatedMessage>;
  onPinDeleted?: MessageHandler<PinDeletedMessage>;
  onPinDiscovered?: MessageHandler<PinDiscoveredMessage>;
  onItemFound?: MessageHandler<ItemFoundMessage>;
  onHunterCompleted?: MessageHandler<HunterCompletedMessage>;
  onHuntCompleted?: MessageHandler<HuntCompletedMessage>;
  onHuntDeleted?: MessageHandler<HuntDeletedMessage>;
  onConnectionStatusChanged?: MessageHandler<ConnectionStatusMessage>;
  // Challenge event handlers
  onChallengeEncountered?: MessageHandler<ChallengeEncounteredMessage>;
  onChallengeAttempt?: MessageHandler<ChallengeAttemptMessage>;
  onChallengeSolved?: MessageHandler<ChallengeSolvedMessage>;
  // Hunt starting/cancelled handlers
  onHuntStarting?: MessageHandler<HuntStartingMessage>;
  onStartCancelled?: MessageHandler<StartCancelledMessage>;
  // Validation handlers - for hider referee feature
  onValidationRequested?: MessageHandler<ValidationRequestedMessage>;
  onValidationApproved?: MessageHandler<ValidationApprovedMessage>;
  onValidationApprovedWithChallenge?: MessageHandler<ValidationApprovedWithChallengeMessage>;
  onValidationRejected?: MessageHandler<ValidationRejectedMessage>;
}

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/**
 * SignalR connection manager for real-time hunt updates
 */
class SignalRConnection {
  private connection: signalR.HubConnection | null = null;
  private huntId: string | null = null;
  private handlers: SignalREventHandlers = {};
  private dynamicHandlers: Map<string, ((data: unknown) => void)[]> = new Map();
  private statusCallbacks: Set<(status: ConnectionStatus) => void> = new Set();
  private reconnectCallbacks: Set<() => void> = new Set();
  private maxReconnectAttempts = 10;
  private connectingPromise: Promise<boolean> | null = null; // Lock to prevent race conditions
  private connectingHuntId: string | null = null; // Track which hunt we're connecting to
  private pendingHandlers: SignalREventHandlers[] = []; // Queue handlers while connecting
  private backgroundReconnectInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectAttemptCount = 0;

  /**
   * Get current connection status
   */
  get status(): ConnectionStatus {
    if (!this.connection) return 'disconnected';
    
    switch (this.connection.state) {
      case signalR.HubConnectionState.Connected:
        return 'connected';
      case signalR.HubConnectionState.Connecting:
        return 'connecting';
      case signalR.HubConnectionState.Reconnecting:
        return 'reconnecting';
      default:
        return 'disconnected';
    }
  }

  /**
   * Subscribe to connection status changes
   */
  onStatusChange(callback: (status: ConnectionStatus) => void): () => void {
    this.statusCallbacks.add(callback);
    return () => this.statusCallbacks.delete(callback);
  }

  /**
   * Subscribe to reconnection events (fired after successful reconnect)
   */
  onReconnected(callback: () => void): () => void {
    this.reconnectCallbacks.add(callback);
    return () => this.reconnectCallbacks.delete(callback);
  }

  /**
   * Get current reconnect attempt count (for UI display)
   */
  get currentReconnectAttempt(): number {
    return this.reconnectAttemptCount;
  }

  /**
   * Force a reconnect attempt (for manual retry button)
   */
  async forceReconnect(): Promise<boolean> {
    if (this.status === 'connected') return true;
    if (!this.huntId) return false;
    
    // Clear any background retry loop
    this.clearBackgroundReconnect();
    
    // If we have a connection object, try to restart it
    if (this.connection) {
      try {
        this.reconnectAttemptCount = 0;
        this.notifyStatusChange();
        await this.connection.start();
        console.log('[SignalR] Force reconnect successful');
        this.notifyStatusChange();
        await this.joinHuntGroup();
        this.notifyReconnected();
        return true;
      } catch (error) {
        console.error('[SignalR] Force reconnect failed:', error);
        // Fall through to start background retry
        this.startBackgroundReconnect();
        return false;
      }
    }
    return false;
  }

  private notifyStatusChange(): void {
    const currentStatus = this.status;
    this.statusCallbacks.forEach((cb) => cb(currentStatus));
  }

  private notifyReconnected(): void {
    this.reconnectCallbacks.forEach((cb) => {
      try {
        cb();
      } catch (err) {
        console.error('[SignalR] Error in reconnect callback:', err);
      }
    });
  }

  /**
   * Start infinite background reconnect loop after automatic retries exhausted
   */
  private startBackgroundReconnect(): void {
    if (this.backgroundReconnectInterval) return; // Already running
    
    console.log('[SignalR] Starting background reconnect loop (every 30s)');
    this.backgroundReconnectInterval = setInterval(async () => {
      if (!this.connection || this.status === 'connected') {
        this.clearBackgroundReconnect();
        return;
      }
      
      this.reconnectAttemptCount++;
      console.log(`[SignalR] Background reconnect attempt ${this.reconnectAttemptCount}`);
      
      try {
        await this.connection.start();
        console.log('[SignalR] Background reconnect successful');
        this.clearBackgroundReconnect();
        this.notifyStatusChange();
        await this.joinHuntGroup();
        this.notifyReconnected();
      } catch {
        // Still disconnected — will retry on next interval
        console.log('[SignalR] Background reconnect failed, will retry...');
        this.notifyStatusChange();
      }
    }, 30000);
  }

  /**
   * Clear the background reconnect loop
   */
  private clearBackgroundReconnect(): void {
    if (this.backgroundReconnectInterval) {
      clearInterval(this.backgroundReconnectInterval);
      this.backgroundReconnectInterval = null;
      this.reconnectAttemptCount = 0;
    }
  }

  /**
   * Connect to SignalR hub for a specific hunt
   */
  async connect(huntId: string, handlers: SignalREventHandlers): Promise<boolean> {
    console.log('[SignalR] connect() called for hunt:', huntId, 'has challengeEncountered:', !!handlers.onChallengeEncountered);
    
    // FIRST: Check if we're currently connecting to this hunt (check this BEFORE anything else)
    // This prevents race conditions where two callers both start connecting
    if (this.connectingPromise && this.connectingHuntId === huntId) {
      console.log('[SignalR] Connection in progress for this hunt, queuing handlers for merge');
      this.pendingHandlers.push(handlers);
      return this.connectingPromise;
    }
    
    // If already connected/connecting to the same hunt, just merge handlers
    if (this.connection && this.huntId === huntId && 
        (this.status === 'connected' || this.status === 'connecting' || this.status === 'reconnecting')) {
      console.log('[SignalR] Already connected/connecting to hunt, merging handlers');
      console.log('[SignalR] Before merge, challengeEncountered:', !!this.handlers.onChallengeEncountered);
      console.log('[SignalR] Incoming handlers have challengeEncountered:', !!handlers.onChallengeEncountered);
      this.handlers = { ...this.handlers, ...handlers };
      console.log('[SignalR] After merge, challengeEncountered handler exists:', !!this.handlers.onChallengeEncountered);
      return this.status === 'connected';
    }

    // SET THE LOCK IMMEDIATELY - BEFORE any await calls!
    // This is critical: await yields execution, allowing other callers to enter
    // By setting the lock first, we ensure other callers see it and queue their handlers
    this.connectingHuntId = huntId;
    this.pendingHandlers = [handlers]; // Start with current handlers in pending
    
    // Create the promise synchronously so subsequent calls see it immediately
    let resolveConnect: (value: boolean) => void;
    this.connectingPromise = new Promise<boolean>((resolve) => {
      resolveConnect = resolve;
    });
    
    console.log('[SignalR] Starting new connection for hunt:', huntId);

    // NOW we can do async operations - disconnect from any existing connection
    if (this.connection) {
      await this.disconnect();
    }

    try {
      const result = await this.doConnect(huntId);
      resolveConnect!(result);
      return result;
    } catch (error) {
      resolveConnect!(false);
      throw error;
    } finally {
      this.connectingPromise = null;
      this.connectingHuntId = null;
    }
  }
  
  /**
   * Internal method to perform the actual connection
   */
  private async doConnect(huntId: string): Promise<boolean> {
    // Merge all pending handlers - this includes the original caller's handlers
    // plus any handlers that were queued while we were setting up
    this.handlers = {};
    for (const pendingHandler of this.pendingHandlers) {
      this.handlers = { ...this.handlers, ...pendingHandler };
    }
    console.log('[SignalR] Merged', this.pendingHandlers.length, 'handler sets, challengeEncountered exists:', !!this.handlers.onChallengeEncountered);
    // Clear pending handlers so we can track NEW handlers that arrive during async operations
    this.pendingHandlers = [];
    this.huntId = huntId;
    
    try {
      // Get SignalR connection info from server
      const response = await apiClient.post<{ url: string; accessToken: string }>(
        '/negotiate',
        { huntId }
      );

      if (!response.success || !response.data) {
        console.error('Failed to negotiate SignalR connection');
        return false;
      }

      const { url, accessToken } = response.data;

      // Create the connection
      // Retry policy: 1s, 2s, 4s, 8s, 16s, 30s, 30s, 30s, 60s, 60s (~4 min total)
      const retryDelays = [1000, 2000, 4000, 8000, 16000, 30000, 30000, 30000, 60000, 60000];
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => accessToken,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            this.reconnectAttemptCount = retryContext.previousRetryCount + 1;
            if (retryContext.previousRetryCount >= this.maxReconnectAttempts) {
              return null; // Stop automatic reconnecting — background loop will take over
            }
            return retryDelays[retryContext.previousRetryCount] ?? 60000;
          },
        })
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Set up connection event handlers
      this.connection.onclose((error) => {
        console.log('[SignalR] Connection closed', error);
        this.notifyStatusChange();
        // Start infinite background retry after automatic retries exhausted
        if (this.huntId) {
          this.startBackgroundReconnect();
        }
      });

      this.connection.onreconnecting((error) => {
        console.log('[SignalR] Reconnecting', error);
        this.notifyStatusChange();
      });

      this.connection.onreconnected(async (connectionId) => {
        console.log('[SignalR] Reconnected', connectionId);
        this.reconnectAttemptCount = 0;
        this.notifyStatusChange();
        // Rejoin the group after reconnection
        await this.joinHuntGroup();
        // Notify subscribers to re-fetch state
        this.notifyReconnected();
      });

      // Register message handlers
      this.registerHandlers();

      // Start the connection
      await this.connection.start();
      console.log('SignalR connected');
      this.notifyStatusChange();

      // Join the hunt group
      await this.joinHuntGroup();
      
      // Merge any NEW pending handlers that were queued while we were doing async operations
      // (the initial handlers were already merged at the start of doConnect)
      if (this.pendingHandlers.length > 0) {
        console.log('[SignalR] Merging', this.pendingHandlers.length, 'additional handler sets that arrived during connection');
        for (const pendingHandler of this.pendingHandlers) {
          console.log('[SignalR] Merging pending handlers, has challengeEncountered:', !!pendingHandler.onChallengeEncountered);
          this.handlers = { ...this.handlers, ...pendingHandler };
        }
        console.log('[SignalR] After merging all, challengeEncountered handler exists:', !!this.handlers.onChallengeEncountered);
        this.pendingHandlers = [];
      }

      return true;
    } catch (error) {
      console.error('Failed to connect to SignalR:', error);
      this.notifyStatusChange();
      return false;
    }
  }

  /**
   * Register all message handlers
   */
  private registerHandlers(): void {
    if (!this.connection) return;

    this.connection.on('locationUpdate', (message: LocationUpdateMessage) => {
      this.handlers.onLocationUpdate?.(message);
    });

    this.connection.on('huntStateChanged', (message: HuntStateChangedMessage) => {
      this.handlers.onHuntStateChanged?.(message);
    });

    this.connection.on('participantJoined', (message: ParticipantJoinedMessage) => {
      this.handlers.onParticipantJoined?.(message);
    });

    this.connection.on('participantLeft', (message: ParticipantLeftMessage) => {
      this.handlers.onParticipantLeft?.(message);
    });

    this.connection.on('participantRemoved', (message: ParticipantRemovedMessage) => {
      this.handlers.onParticipantRemoved?.(message);
    });

    this.connection.on('participantReady', (message: ParticipantReadyMessage) => {
      this.handlers.onParticipantReady?.(message);
    });

    this.connection.on('pinCreated', (message: PinCreatedMessage) => {
      this.handlers.onPinCreated?.(message);
    });

    this.connection.on('pinDeleted', (message: PinDeletedMessage) => {
      this.handlers.onPinDeleted?.(message);
    });

    this.connection.on('pinDiscovered', (message: PinDiscoveredMessage) => {
      console.log('[SignalR] Received pinDiscovered:', message);
      console.log('[SignalR] Handler exists:', !!this.handlers.onPinDiscovered);
      this.handlers.onPinDiscovered?.(message);
    });

    this.connection.on('itemFound', (message: ItemFoundMessage) => {
      this.handlers.onItemFound?.(message);
    });

    this.connection.on('hunterCompletedHunt', (message: HunterCompletedMessage) => {
      this.handlers.onHunterCompleted?.(message);
    });

    this.connection.on('huntCompleted', (message: HuntCompletedMessage) => {
      console.log('[SignalR] Received huntCompleted:', message);
      this.handlers.onHuntCompleted?.(message);
    });

    this.connection.on('huntDeleted', (message: HuntDeletedMessage) => {
      this.handlers.onHuntDeleted?.(message);
    });

    this.connection.on('connectionStatusChanged', (message: ConnectionStatusMessage) => {
      this.handlers.onConnectionStatusChanged?.(message);
    });

    // Challenge event handlers
    this.connection.on('challengeEncountered', (message: ChallengeEncounteredMessage) => {
      console.log('[SignalR] Received challengeEncountered:', message);
      console.log('[SignalR] Handler exists for challengeEncountered:', !!this.handlers.onChallengeEncountered);
      this.handlers.onChallengeEncountered?.(message);
    });

    this.connection.on('challengeAttempt', (message: ChallengeAttemptMessage) => {
      console.log('[SignalR] Received challengeAttempt:', message);
      console.log('[SignalR] Handler exists for challengeAttempt:', !!this.handlers.onChallengeAttempt);
      this.handlers.onChallengeAttempt?.(message);
    });

    this.connection.on('challengeSolved', (message: ChallengeSolvedMessage) => {
      console.log('[SignalR] Received challengeSolved:', message);
      this.handlers.onChallengeSolved?.(message);
    });

    // Hunt starting/cancelled handlers
    this.connection.on('huntStarting', (message: HuntStartingMessage) => {
      console.log('[SignalR] Received huntStarting:', message);
      this.handlers.onHuntStarting?.(message);
    });

    this.connection.on('startCancelled', (message: StartCancelledMessage) => {
      console.log('[SignalR] Received startCancelled:', message);
      this.handlers.onStartCancelled?.(message);
    });

    // Validation handlers - for hider referee feature
    this.connection.on('validationRequested', (message: ValidationRequestedMessage) => {
      console.log('[SignalR] Received validationRequested:', message);
      this.handlers.onValidationRequested?.(message);
    });

    this.connection.on('validationApproved', (message: ValidationApprovedMessage) => {
      console.log('[SignalR] Received validationApproved:', message);
      this.handlers.onValidationApproved?.(message);
    });

    this.connection.on('validationApprovedWithChallenge', (message: ValidationApprovedWithChallengeMessage) => {
      console.log('[SignalR] Received validationApprovedWithChallenge:', message);
      this.handlers.onValidationApprovedWithChallenge?.(message);
    });

    this.connection.on('validationRejected', (message: ValidationRejectedMessage) => {
      console.log('[SignalR] Received validationRejected:', message);
      this.handlers.onValidationRejected?.(message);
    });

    // Register any dynamic handlers that were added before connect
    this.dynamicHandlers.forEach((handlers, eventName) => {
      handlers.forEach(handler => {
        this.connection!.on(eventName, handler);
      });
    });
  }

  /**
   * Join the SignalR group for the current hunt
   */
  private async joinHuntGroup(): Promise<void> {
    if (!this.huntId) return;

    try {
      const response = await apiClient.post('/signalr/join-group', { huntId: this.huntId });
      console.log('[SignalR] Joined hunt group:', this.huntId, response);
    } catch (error) {
      console.error('Failed to join hunt group:', error);
    }
  }

  /**
   * Register a dynamic handler for a message type
   * Allows adding handlers after connection is established
   */
  registerHandler(eventName: string, handler: (data: unknown) => void): void {
    const existing = this.dynamicHandlers.get(eventName) || [];
    existing.push(handler);
    this.dynamicHandlers.set(eventName, existing);

    // If already connected, register with SignalR hub
    if (this.connection) {
      this.connection.on(eventName, handler);
    }
  }

  /**
   * Remove a dynamic handler for a message type
   */
  removeHandler(eventName: string, handler?: (data: unknown) => void): void {
    if (handler) {
      const existing = this.dynamicHandlers.get(eventName) || [];
      const filtered = existing.filter(h => h !== handler);
      if (filtered.length > 0) {
        this.dynamicHandlers.set(eventName, filtered);
      } else {
        this.dynamicHandlers.delete(eventName);
      }
      if (this.connection) {
        this.connection.off(eventName, handler);
      }
    } else {
      // Remove all handlers for this event
      this.dynamicHandlers.delete(eventName);
      if (this.connection) {
        this.connection.off(eventName);
      }
    }
  }

  /**
   * Send location update to other participants
   */
  async sendLocation(location: {
    latitude: number;
    longitude: number;
    accuracy: number;
    heading?: number;
    speed?: number;
  }): Promise<boolean> {
    if (!this.huntId || this.status !== 'connected') {
      return false;
    }

    try {
      await apiClient.post(`/hunts/${this.huntId}/broadcast-location`, location);
      return true;
    } catch (error) {
      console.error('Failed to send location:', error);
      return false;
    }
  }

  /**
   * Disconnect from SignalR
   */
  async disconnect(): Promise<void> {
    this.clearBackgroundReconnect();
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('Error disconnecting from SignalR:', error);
      }
      this.connection = null;
    }
    this.huntId = null;
    this.handlers = {};
    this.dynamicHandlers.clear();
    this.reconnectCallbacks.clear();
    this.notifyStatusChange();
  }
}

// Singleton instance
export const signalRConnection = new SignalRConnection();
